import apiRequest from './client';

export const searchBanks = (username = '') =>
  apiRequest(`/banks/?username=${encodeURIComponent(username)}`);

// Resout un Bank par son username EXACT (8 chiffres, celui affiche/tape sur le
// POS) -> {id, name, username} ou null. `searchBanks` matche en icontains
// (username OU nom) donc peut renvoyer plusieurs lignes : on ne garde que la
// correspondance username exacte, sinon "introuvable" plutot qu'un choix ambigu.
export const resolveBankByUsername = async (username) => {
  const u = String(username ?? '').trim();
  if (!u) return null;
  const results = await searchBanks(u).catch(() => []);
  return results.find((r) => r.username === u) || null;
};

// Superviseurs (entite dediee) de la company — pour le filtre de scope "Supervisor".
export const searchSupervisors = () => apiRequest('/supervisors/');

export const getBank = (id) => apiRequest(`/banks/${id}/`);

// Resout un Bank par son numero AFFICHE (Bank.seq — propre a la company, pas
// l'id Django interne, cf. banks/models.py) -> premiere correspondance ou
// null. `?seq=` est deja scope a la company connectee cote backend
// (BankViewSet.get_queryset), donc jamais d'ambiguite avec le seq d'un bank
// d'une autre company.
export const resolveBankBySeq = async (seq) => {
  const s = String(seq ?? '').trim();
  if (!s) return null;
  const results = await apiRequest(`/banks/?seq=${encodeURIComponent(s)}`).catch(() => []);
  return results[0] || null;
};

// Resout un Bank tape a la main par n'importe lequel des 2 identifiants que
// l'utilisateur peut avoir sous les yeux : le username (8 chiffres, POS) EN
// PRIORITE, sinon le numero AFFICHE (Bank.seq, visible dans certains tableaux,
// ex. colonne "ID" de Act/Desactiver Ventes Manuel) si la saisie est purement
// numerique. Evite le faux "Banque introuvable" quand l'utilisateur retape un
// numero qu'il a vu affiche ailleurs plutot que le username.
export const resolveBankByIdentifier = async (value) => {
  const v = String(value ?? '').trim();
  if (!v) return null;
  const byUsername = await resolveBankByUsername(v);
  if (byUsername) return byUsername;
  const seq = normalizeBankSeq(v);
  return seq ? resolveBankBySeq(seq) : null;
};

// N° de banque tel qu'on le lit sur la fiche papier -> Bank.seq ("19"), ou null.
// Le POS imprime "REF: 19-19" / "REF: 05-05" (seq repete, sur 2 chiffres, cf.
// lottery-app printer.js) : on accepte "19-19", "05-05", "05", "5".
export const normalizeBankSeq = (value) => {
  const m = String(value ?? '').trim().match(/^0*(\d+)(?:\s*-\s*0*(\d+))?$/);
  if (!m || (m[2] !== undefined && m[2] !== m[1])) return null;
  return m[1];
};

export const updateBank = (id, data) =>
  apiRequest(`/banks/${id}/`, { method: 'PATCH', body: data });

export const listGroups = () => apiRequest('/bank-groups/');

export const getGroup = (id) => apiRequest(`/bank-groups/${id}/`);

// POST /api/banks/<id>/set-password/ (Central > Changer Terminal Password) —
// remplace le mot de passe deterministe habituel du bank par un mot de passe
// choisi par la company. Renvoie { detail } ou leve avec {password|password_confirm}
// en cas d'erreur (mismatch, trop court).
export const setBankPassword = (id, { password, password_confirm }) =>
  apiRequest(`/banks/${id}/set-password/`, { method: 'POST', body: { password, password_confirm } });

// GET /api/banks/bulk-status/ (Parametres > Act/Desactiver Ventes (Manuel)) :
// liste les banks du scope choisi avec leur statut ACTUEL (sale_enabled/
// login_blocked) — pour voir qui est deja bloque avant/apres d'agir.
// filter = 'All'|'Group'|'Bank', scope = id du group/bank (omis pour 'All').
export const fetchBankStatusList = ({ filter, scope }) => {
  const params = new URLSearchParams({ filter });
  if (scope) params.set('scope', scope);
  return apiRequest(`/banks/bulk-status/?${params.toString()}`);
};

// POST /api/banks/bulk-status/ : applique une action a TOUS les banks du scope
// choisi en un seul appel. action = 'block_sale'|'unblock_sale'|'block_login'|
// 'unblock_login', filter = 'All'|'Group'|'Bank', scope = id du group/bank
// (omis pour 'All'). Renvoie { updated: <nombre de banks touches> }.
export const bulkBankStatus = ({ action, filter, scope }) =>
  apiRequest('/banks/bulk-status/', { method: 'POST', body: { action, filter, scope } });

// Solde prepaye (demande explicite 2026-09-19) : une fois Bank.is_balance
// active, le bank ne peut vendre que jusqu'a concurrence de son solde —
// deposit ci-dessous credite Bank.balance et cree un BankDeposit (historique
// consultable ici ET par le bank lui-meme dans l'app, Reports > Recharge).
export const listDeposits = (bankId) =>
  apiRequest(`/banks/deposits/${bankId ? `?bank=${bankId}` : ''}`);

export const makeDeposit = (bankId, { amount, note }) =>
  apiRequest('/banks/deposits/', { method: 'POST', body: { bank: bankId, amount, note } });

// "Supprimer" une banque cote company = l'ARCHIVER (2026-09-26) : cachee,
// connexion + vente bloquees, fiches conservees ; seul le superadmin peut la restaurer.
export const archiveBank = (id) => apiRequest(`/banks/${id}/archive/`, { method: 'POST' });
